import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { activeClientState } from '../../state.js';
import { api } from '../../api.js';
import { Card } from '../Card.js';
import { ErrorBanner } from '../FormBits.js';
import { Tabs, useTabParam } from '../Tabs.js';
import { Table, THeadRow, TH, TD, TR } from '../ui/table.js';
import { fmtIls } from '../../utils.js';
import { PolicyRulesTab, type FundOpt } from './PolicyRulesTab.js';
import { PolicyMonthTab } from './PolicyMonthTab.js';
import { policy, type BankCategory } from './policyApi.js';
import { SLEEVE_BY_ID, type PolicyRuleRow, type SleeveId } from '@agio/core/domain';
import { EmptyState } from '../Card.js';

const TABS = ['rules', 'month', 'labels'] as const;

/**
 * מדיניות השקעות: set the client's rules up once, then check and publish the
 * control table month by month. The rules come first and gate the rest —
 * without a single rule there is nothing to compute and nothing to look at.
 * The third tab lists every sub-sleeve label the banks' statements print —
 * the raw material behind the derived measures, so an admin can see what
 * "hedged" or "rated" actually covered.
 */
export function PolicyCard() {
  // The client picked in the header — every admin screen follows that one.
  const clientId = useRecoilValue(activeClientState) ?? '';
  const [tab, setTab] = useTabParam(TABS);
  const [funds, setFunds] = useState<FundOpt[]>([]);
  const [rules, setRules] = useState<PolicyRuleRow[] | null>(null);
  const [rulesTick, setRulesTick] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cleared first: switching client in the header must not leave the previous
    // client's rules on screen while the new ones load.
    setFunds([]);
    setRules(null);
    setError(null);
    if (!clientId) return;
    let live = true;
    api.get<FundOpt[]>(`/admin/clients/${clientId}/funds`).then((f) => live && setFunds(f)).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    let live = true;
    policy.rules(clientId).then((r) => live && setRules(r)).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId, rulesTick]);

  const hasRules = (rules?.length ?? 0) > 0;
  // A tab that has nothing to show yet cannot stay selected (a bookmark, say).
  useEffect(() => {
    if (rules !== null && !hasRules && tab !== 'rules') setTab('rules');
  }, [rules, hasRules, tab, setTab]);

  return (
    <Card>
      <div className="grid gap-5">
        {!clientId && <EmptyState label="בחרו לקוח למעלה" />}
        <ErrorBanner error={error} title="שגיאה" />
        {clientId && (
          <>
            <Tabs
              tabs={[
                // Lines of the policy, not versions: a limit that changed is still one rule.
                { id: 'rules', label: 'כללי המדיניות', count: hasRules ? new Set(rules!.map((r) => r.line_id)).size : undefined },
                { id: 'month', label: 'בקרה חודשית', disabled: !hasRules },
                { id: 'labels', label: 'תת-אפיקים בדוחות הבנקים', disabled: !hasRules },
              ]}
              active={tab}
              onChange={setTab}
            />
            {tab === 'rules' && rules !== null && (
              <PolicyRulesTab clientId={clientId} funds={funds} rules={rules} onChanged={() => setRulesTick((t) => t + 1)} />
            )}
            {tab === 'month' && hasRules && <PolicyMonthTab clientId={clientId} />}
            {tab === 'labels' && hasRules && <BankLabelsTab clientId={clientId} />}
          </>
        )}
      </div>
    </Card>
  );
}

function BankLabelsTab({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<BankCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setRows(null);
    policy.bankCategories(clientId).then((r) => live && setRows(r)).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId]);
  return (
    <div className="grid gap-3">
      <p className="text-sm text-muted-foreground">
        כל בנק מסווג את ניירות הערך בדוח שלו לתת-אפיקים משלו (למשל "קרנות מחקות חו"ל מנוטרל מט"ח" או "אג"ח קונצרני צמוד מדד דירוג גבוה").
        הטבלה מאחדת את כל התת-אפיקים האלה מהדוח האחרון של כל תיק, ומראה כמה ניירות בכל אחד סומנו כמנוטרלי מט"ח, כשווקים מתעוררים, ולכמה יש דירוג ומח"מ —
        כלומר, על מה בדיוק נשענים המדדים הנגזרים (חשיפה למט"ח, דירוג, מח"מ) אצל הלקוח הזה, ואיפה הדוח פשוט לא מספק את הנתון.
      </p>
      <ErrorBanner error={error} title="שגיאה" />
      {rows && rows.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">אין עדיין פירוט תת-אפיקים — הוא נשמר מדוחות חודשיים שנקלטו לאחר עדכון זה.</p>}
      {rows && rows.length > 0 && (
        <Table minWidth="48rem">
          <THeadRow>
            <TH>תת-אפיק בדוח הבנק</TH><TH>אפיק</TH><TH align="end">ניירות</TH><TH align="end">שווי</TH><TH>תיקים</TH><TH align="end">מנוטרל מט"ח</TH><TH align="end">שווקים מתעוררים</TH><TH align="end">עם דירוג</TH><TH align="end">עם מח"מ</TH>
          </THeadRow>
          <tbody>
            {rows.map((r) => (
              <TR key={`${r.bank_category}|${r.sleeve}`}>
                <TD className="font-medium">{r.bank_category}</TD>
                <TD className="text-xs text-muted-foreground">{r.sleeve ? SLEEVE_BY_ID[r.sleeve as SleeveId]?.displayNameHe ?? r.sleeve : '—'}</TD>
                <TD align="end" className="tabular-nums">{r.positions}</TD>
                <TD align="end" className="tabular-nums">{fmtIls(r.value)}</TD>
                <TD className="text-xs">{r.funds.join(', ')}</TD>
                <TD align="end" className="tabular-nums">{r.hedged || '—'}</TD>
                <TD align="end" className="tabular-nums">{r.emerging || '—'}</TD>
                <TD align="end" className="tabular-nums">{r.rated || '—'}</TD>
                <TD align="end" className="tabular-nums">{r.with_duration || '—'}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
